
<template>
  <h2>
    <span>{{ monthNames[currentMonth] }} {{ currentYear }}</span>
    <button
      @click="generatePDF"
      class="button-save-outline"
      :disabled="!hasTicketsForSelected"
      :title="t('generate-pdf')"
    >
      <span>{{ t('generate-pdf') }}</span>
    </button>
  </h2>
  
  <!-- Tabela z ticketami (zgłoszeniami awarii) -->
  <table class="tickets-table">
    <thead>
      <tr>
        <th>{{ t('ordinal-number') }}</th>
        <th>{{ t('failure-description') }}</th>
        <th>{{ t('failure-report-date') }}</th>
        <th>{{ t('failure-removal-report-date') }}</th>
        <th>{{ t('failure-duration') }}</th>
        <th>{{ t('total-duration') }}</th>
        <th>{{ t('action') }}</th>
      </tr>
    </thead>

    <!-- Dane -->
    <tbody v-if="hasTicketsForSelected">
      <tr
        v-for="(ticket, index) in paginatedTickets"
        :key="ticket.id"
        class="clickable-row"
        @click="goToDetails(ticket.id)"
        :title="t('ticket-see-details', { id: ticket.id })"
      >
        <td>{{ index + 1 }}</td>
        <!-- Tytuł zgłoszenia jest zapisywany w bazie (po polsku) — tłumaczy go słownik `db`. -->
        <td>{{ tdb(ticket.title) }}</td>
        <td>{{ formatDate(ticket.created_at) }}</td>
        <td>{{ formatDate(ticket.closed_at) || t('not-completed') }}</td>
        <td>{{ calculateDowntime(ticket.created_at, ticket.closed_at) }}</td>
        <td>{{ totalDowntime(paginatedTickets) }}</td>
        <td>
          <button @click.stop="goToEdit(ticket.id)" class="btn button-edit">{{t('edit')}}</button>
        </td>
      </tr>
    </tbody>

    <!-- Pusty stan -->
    <tbody v-else>
      <tr>
        <td :colspan="7" class="empty-cell">
          {{ t('no-reports-for-period') }}
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Sekcja wyboru daty: wybór roku i miesiąca -->
  <div class="date-picker">
    <div class="year-selector">
      <button
        v-for="year in availableYears"
        :key="year"
        :class="{ active: year === currentYear }"
        @click="selectYear(year)"
      >
        {{ year }}
      </button>
    </div>
    <div class="month-selector">
      <button
        v-for="month in availableMonths"
        :key="month.index"
        :class="{ active: month.index === currentMonth }"
        @click="selectMonth(month.index)"
      >
        {{ month.name }}
      </button>
    </div>
  </div>
</template>

<script>
import TicketsService from '../services/TicketsService.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { i18n } from '@/main';
import { tdb } from '@/helpers/dbTranslate';

// Wbudowane fonty jsPDF obsługują tylko Latin-1 — polskie „ł”, „ż” czy cyrylica wyszłyby
// w PDF jako śmieci. Dlatego teksty raportu przepuszczamy przez `normalizeForPdf`, a gdy
// alfabetu nie da się zapisać (np. ua), raport składamy po angielsku.
const PDF_FALLBACK_LOCALE = 'en';

export default {
  name: "TicketsTable",
  data() {
    return {
      tickets: [], // wszystkie zgłoszenia
      currentMonth: null, // aktualnie wybrany miesiąc
      currentYear: null,  // aktualnie wybrany rok
    };
  },
  mounted() {
    this.fetchTickets(); // pobiera tickety po zamontowaniu komponentu
  },
  computed: {
    // Nazwy miesięcy w aktualnym języku (indeks 0 = styczeń, jak w Date#getMonth).
    monthNames() {
      return Array.from({ length: 12 }, (_, index) => this.t(`month-${index + 1}`));
    },
    // Zwraca tylko tickety z wybranego miesiąca i roku
    paginatedTickets() {
      return this.tickets.filter(ticket => {
        const createdAt = new Date(ticket.created_at);
        return createdAt.getMonth() === this.currentMonth && createdAt.getFullYear() === this.currentYear;
      });
    },
    hasAnyTickets() {
      return this.tickets.length > 0;
    },
    hasTicketsForSelected() {
      return this.paginatedTickets.length > 0;
    },
    headerLabel() {
      return this.currentMonth !== null && this.currentYear !== null
        ? `${this.monthNames[this.currentMonth]} ${this.currentYear}`
        : this.t('reports');
    },
    // Lista dostępnych lat z ticketa
    availableYears() {
      const years = this.tickets.map(ticket => new Date(ticket.created_at).getFullYear());
      return [...new Set(years)].sort((a, b) => a - b);
    },
    // Lista dostępnych miesięcy dla wybranego roku
    availableMonths() {
      const monthsWithTickets = this.tickets
        .filter(ticket => new Date(ticket.created_at).getFullYear() === this.currentYear)
        .map(ticket => new Date(ticket.created_at).getMonth());

      if (monthsWithTickets.length === 0) return [];

      const minMonth = Math.min(...monthsWithTickets);
      const maxMonth = Math.max(...monthsWithTickets);

      return this.monthNames
        .map((name, index) => ({ name, index }))
        .filter(month => month.index >= minMonth && month.index <= maxMonth);
    }
  },
  methods: {
    // Pobiera tickety przez serwis i ustawia domyślnie najnowszy rok/miesiąc
    async fetchTickets() {
        TicketsService.getTickets()
        .then((response) => {
            this.tickets = response;

            if (this.tickets.length > 0) {
                const latestTicket = this.tickets.reduce((latest, ticket) => {
                  const ticketDate = new Date(ticket.created_at);
                  return ticketDate > new Date(latest.created_at) ? ticket : latest;
                });

                this.currentYear = new Date(latestTicket.created_at).getFullYear();
                this.currentMonth = new Date(latestTicket.created_at).getMonth();
            }
        })
        .catch((error) => {
            console.error("Błąd podczas pobierania danych:", error);
        });
    },
    // Ustawia wybrany miesiąc
    selectMonth(index) {
      this.currentMonth = index;
    },
    // Ustawia wybrany rok (oraz pierwszy dostępny miesiąc)
    selectYear(year) {
      this.currentYear = year;
      this.currentMonth = this.availableMonths.length > 0 ? this.availableMonths[0].index : 0;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} `
          + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    // różnica w MINUTACH (z ochroną przed wartościami < 0)
    diffMinutes(start, end) {
      if (!start) return 0;
      const from = new Date(start);
      const to = end ? new Date(end) : new Date();
      const ms = to - from;
      return Math.max(0, Math.floor(ms / 60000));
    },
    /**
     * "43 min", "1 godz. 20 min", a po 24h: "2 dni 3 godz. 5 min" — jednostki z i18n.
     *
     * @param {number} mins Czas trwania w minutach.
     * @param {function(string): string} translate Sposób pobrania jednostki. Raport PDF
     *        podaje tu `pdfText`, bo wbudowany font nie zapisze np. cyrylicy.
     */
    formatDuration(mins, translate = (key) => this.t(key)) {
      const unitMinutes = translate('unit-minutes-short');
      const unitHours = translate('unit-hours-short');

      if (mins < 60) return `${mins} ${unitMinutes}`;

      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;

      // < 24h → godziny i minuty
      if (hours < 24) {
        return minutes ? `${hours} ${unitHours} ${minutes} ${unitMinutes}` : `${hours} ${unitHours}`;
      }

      // ≥ 24h → dni, godziny, minuty
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;

      let out = `${days} ${days === 1 ? translate('unit-day') : translate('unit-days')}`;
      if (remHours) out += ` ${remHours} ${unitHours}`;
      if (minutes)  out += ` ${minutes} ${unitMinutes}`;
      return out;
    },

    // czas trwania jednego zgłoszenia – zwraca gotowy string
    calculateDowntime(createdAt, closeAt, translate) {
      const mins = this.diffMinutes(createdAt, closeAt);
      return this.formatDuration(mins, translate);
    },

    // łączny czas trwania dla listy zgłoszeń – też gotowy string
    totalDowntime(tickets, translate) {
      const totalMins = tickets.reduce(
        (sum, t) => sum + this.diffMinutes(t.created_at, t.closed_at),
        0
      );
      return this.formatDuration(totalMins, translate);
    },
    /**
     * Sprowadza tekst do zakresu obsługiwanego przez wbudowany font jsPDF (Latin-1).
     *
     * @returns {string|null} Tekst bez znaków diakrytycznych albo null, gdy alfabetu
     *                        nie da się zapisać wbudowanym fontem (np. cyrylica).
     */
    normalizeForPdf(text) {
      const stripped = String(text ?? '')
        .replace(/ł/g, 'l')
        .replace(/Ł/g, 'L')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      // Wszystko powyżej U+00FF (cyrylica, znaki spoza Latin-1) jest dla tego fontu nieczytelne.
      const unsupported = Array.from(stripped).some((char) => char.codePointAt(0) > 0xff);

      return unsupported ? null : stripped;
    },
    // Napis z tłumaczeń — gdy język nie mieści się w foncie PDF, bierzemy wersję angielską.
    pdfText(key, params = {}) {
      return this.normalizeForPdf(this.t(key, params))
        ?? this.normalizeForPdf(i18n.global.t(key, params, { locale: PDF_FALLBACK_LOCALE }))
        ?? '';
    },
    // Wartość z danych (np. tytuł zgłoszenia) — fallbackiem jest oryginał z bazy.
    pdfValue(text, fallback) {
      return this.normalizeForPdf(text) ?? this.normalizeForPdf(fallback) ?? '';
    },
    // Generuje PDF z tabelą ticketów
    generatePDF() {
      if (!this.hasTicketsForSelected) return;

      const doc = new jsPDF();
      const pdfUnit = (key) => this.pdfText(key);
      // Nazwa miesiąca w języku UI; gdy font PDF jej nie obsłuży — numer miesiąca.
      const monthForPdf = this.normalizeForPdf(this.monthNames[this.currentMonth])
        ?? String(this.currentMonth + 1);
      const reportTitle = this.pdfText('pdf-report-for-period', {
        month: monthForPdf,
        year: this.currentYear,
      });

      doc.text(reportTitle, 10, 10);

      // Filtrowanie tylko ticketów danej organizacji
      const filteredTickets = this.paginatedTickets.filter(ticket => ticket.organization_id == process.env.ORGANIZATION_ID);

      const tableData = filteredTickets.map((ticket, index) => [
          index + 1,
          this.pdfValue(tdb(ticket.title), ticket.title),
          this.formatDate(ticket.created_at),
          this.formatDate(ticket.closed_at) || this.pdfText('not-completed'),
          this.calculateDowntime(ticket.created_at, ticket.closed_at, pdfUnit),
          this.totalDowntime(filteredTickets, pdfUnit),
      ]);

      autoTable(doc, {
          head: [[
            this.pdfText('ordinal-number'),
            this.pdfText('failure-description'),
            this.pdfText('failure-report-date'),
            this.pdfText('failure-removal-report-date'),
            this.pdfText('failure-duration'),
            this.pdfText('total-duration'),
            this.pdfText('signature-orderer'),
            this.pdfText('signature-contractor'),
          ]],
          body: tableData,
          startY: 20,
      });

      doc.save(`${reportTitle.replace(/\s+/g, '_')}.pdf`);
    },
    goToDetails(id) { 
      this.$router.push({ name: 'ticket-details', params: { id } }); 
    },
    goToEdit(id) { 
      this.$router.push({ name: 'ticket-edit',    params: { id } }); 
    },
  }
};
</script>
<style scoped>
.empty-cell {
  padding: 24px;
  text-align: center;
  color: var(--text-muted);
  background: var(--surface-secondary);
  font-style: italic;
}

.button-save-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.date-picker {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 30px;
}

.year-selector, .month-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.year-selector button, .month-selector button {
  padding: 5px;
  border: 2px solid var(--accent-blue);
  background-color: var(--surface-primary);
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;
}

.year-selector button.active, .month-selector button.active {
  /* background-color: var(--status-ok); */
  background-color: var(--accent-blue);
  color: var(--text-inverse);
  font-weight: var(--font-weight-bold);
  border-radius: 4px;
}

.year-selector button:hover, .month-selector button:hover {
  /* background-color: var(--status-ok); */
  background-color: var(--accent-blue);
  color: var(--text-inverse);
}

.tickets-table {
  width: 100%;
  border-collapse: collapse;
}

.tickets-table th, .tickets-table td {
  border: 1px solid var(--border-subtle);
  padding: 8px;
  text-align: center;
}

.tickets-table th {
  background-color: var(--surface-primary);
  font-weight: var(--font-weight-bold);
}

.tickets-table tr:nth-child(odd) {
  background-color: var(--surface-hover);
}

.tickets-table tr:nth-child(even) {
  background-color: var(--surface-primary);
}

h2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
}

.button-save-outline {
  padding: 0.25rem;
  border-radius: 4px;
  border: 2px solid var(--accent-blue);
  background-color: var(--surface-primary);
  cursor: pointer;
  transition: all 0.3s;
}

.button-save-outline:hover {
  background-color: var(--accent-blue) !important;
  color: var(--text-inverse) !important;
}

.button-save-outline span {
  margin-top: 3px;
}

.clickable-row { cursor: pointer; }

.button-edit {
  padding: 0.5rem 1rem 0.5rem 1rem;
  border-radius: 4px;
}

.button-edit:hover {
  background-color: var(--status-warn-strong) !important;
  color: white !important;
}
</style>